import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ScenarioRunPreflightCheck } from '../../api/api';
import { groupBySystem, withInlineCode } from '../../utils/inlineCode';
import { runMomentShort } from '../../utils/scenarioRunFormat';
import {
    blockedScenarios,
    PreflightChecks,
    PreflightCounts,
    preflightNeedsAttention,
} from './ScenarioPreflight';

export interface IEnvironmentCheckState {
    // 'unavailable' is an environment the dashboard may not run scenarios on, which it therefore does not check.
    status: 'unavailable' | 'checking' | 'done' | 'error';
    checks: ScenarioRunPreflightCheck[];
    checkedAtUtc?: string;
    error?: string;
}

export interface IEnvironmentCheckProps {
    // What the suite asks a person to arrange, as the suite words it.
    steps: string[];
    check: IEnvironmentCheckState;
    onRecheck: () => void;
}

/**
 * Whether the environment is set up for the suite: the checks a run makes before its first scenario, made now.
 *
 * The suite's own list of manual steps stays underneath, because it says what to do about a failed check in the
 * suite author's words. Folded while everything is in order; open by itself when a check failed or could not be
 * made, since that is what decides whether starting a run is worth it.
 */
export const EnvironmentCheck = ({
    steps,
    check,
    onRecheck,
}: IEnvironmentCheckProps) => {
    const [open, setOpen] = useState(false);
    const groups = useMemo(() => groupBySystem(steps), [steps]);
    const blocked = blockedScenarios(check.checks);
    const attention =
        check.status === 'error' ||
        (check.status === 'done' && preflightNeedsAttention(check.checks));

    useEffect(() => {
        if (attention) setOpen(true);
    }, [attention]);

    return (
        <div
            className={`arl-envcheck${
                attention ? ' arl-envcheck--attention' : ''
            }`}
        >
            <button
                type="button"
                className="arl-envcheck__toggle"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
            >
                <FontAwesomeIcon
                    icon={
                        check.status === 'checking'
                            ? 'spinner'
                            : attention
                            ? 'triangle-exclamation'
                            : check.status === 'done'
                            ? 'circle-check'
                            : 'circle-info'
                    }
                    spin={check.status === 'checking'}
                    className="arl-envcheck__icon"
                />
                <strong>Environment</strong>
                <span className="arl-envcheck__summary">
                    {check.status === 'checking' &&
                        'checking Exact and Chargebee…'}
                    {check.status === 'unavailable' &&
                        'checked by every run before its first scenario'}
                    {check.status === 'error' &&
                        `could not be checked: ${check.error}`}
                    {check.status === 'done' && (
                        <>
                            <PreflightCounts checks={check.checks} />
                            {blocked.size > 0 && (
                                <span className="arl-envcheck__blocked">
                                    {blocked.size} scenario(s) would not run
                                </span>
                            )}
                        </>
                    )}
                </span>
                <FontAwesomeIcon
                    icon={open ? 'angle-up' : 'angle-down'}
                    className="arl-envcheck__chevron"
                />
            </button>

            {open && (
                <div className="arl-envcheck__body">
                    {check.status !== 'unavailable' && (
                        <p className="arl-envcheck__note">
                            {check.status === 'checking'
                                ? 'Checking, read-only…'
                                : `Checked${
                                      check.checkedAtUtc
                                          ? ` ${runMomentShort(
                                                check.checkedAtUtc
                                            )}`
                                          : ''
                                  }, read-only.`}{' '}
                            {check.status !== 'checking' && (
                                <button
                                    type="button"
                                    className="arl-link-btn"
                                    onClick={onRecheck}
                                >
                                    Check again
                                </button>
                            )}
                        </p>
                    )}

                    {check.status === 'done' && (
                        <div className="arl-envcheck__checks">
                            <PreflightChecks checks={check.checks} />
                        </div>
                    )}

                    {groups.length > 0 && (
                        <>
                            <div className="arl-envcheck__heading">
                                Arrange by hand
                            </div>
                            <div className="arl-prereq__groups">
                                {groups.map((group) => (
                                    <div
                                        className="arl-prereq__group"
                                        key={group.system}
                                    >
                                        <div className="arl-prereq__system">
                                            {group.system}
                                        </div>
                                        <ul className="arl-prereq__list">
                                            {group.steps.map((step) => (
                                                <li key={step}>
                                                    {withInlineCode(step)}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
